// src/modules/Admin/components/EditFirmModal.jsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  Upload,
  Row,
  Col,
  Card,
  Avatar,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  fetchCountries,
  fetchStates,
  fetchCities,
  updateFirm,
} from "../../../api/firmApi";
import { withBase } from "../../../config/env";

export default function EditFirmModal({ open, onClose, firm, onSuccess }) {
  const [form] = Form.useForm();

  // Image state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Dropdown data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Loading flags
  const [saving, setSaving] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // -------- Helpers --------
  const loadStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    setLoadingStates(true);
    try {
      const res = await fetchStates(countryId);
      setStates(res || []);
    } catch {
      toastError("Failed to load states");
    } finally {
      setLoadingStates(false);
    }
  };

  const loadCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const res = await fetchCities(stateId);
      setCities(res || []);
    } catch {
      toastError("Failed to load cities");
    } finally {
      setLoadingCities(false);
    }
  };

  // -------- Effects: open & firm --------
  useEffect(() => {
    const loadAll = async () => {
      if (!open || !firm) return;

      // Reset form & image
      form.resetFields();
      setImageFile(null);
      setImagePreview(firm.image ? withBase(firm.image) : null);

      // Load countries first
      setLoadingCountries(true);
      try {
        const countryList = (await fetchCountries()) || [];
        setCountries(countryList);
      } catch {
        toastError("Failed to load countries");
      } finally {
        setLoadingCountries(false);
      }

      // Set base scalar fields
      form.setFieldsValue({
        name: firm.name || "",
        email: firm.email || "",
        contact_number: firm.contact_number || "",
        address: firm.address || "",
        status: firm.status || "Active",
        country_id: firm.country_id || "",
        state_id: firm.state_id || "",
        city_id: firm.city_id || "",
        // security fields default empty
        password: "",
        confirm_password: "",
      });

      // Load dependent lists according to existing IDs
      await loadStates(firm.country_id);
      await loadCities(firm.state_id);
    };

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firm]);

  // -------- Handlers: selects --------
  const handleCountryChange = async (countryId) => {
    // set value, clear dependents
    form.setFieldsValue({ country_id: countryId, state_id: "", city_id: "" });
    setStates([]);
    setCities([]);
    await loadStates(countryId);
  };

  const handleStateChange = async (stateId) => {
    form.setFieldsValue({ state_id: stateId, city_id: "" });
    setCities([]);
    await loadCities(stateId);
  };

  // -------- Image upload/preview --------
  const handleImageBeforeUpload = (file) => {
    const isAllowed =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp" ||
      file.type === "image/jpg";
    if (!isAllowed) {
      toastError("Only JPG/PNG/WEBP files are allowed.");
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      toastError("Image must be smaller than 5MB.");
      return Upload.LIST_IGNORE;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    return false; // prevent auto upload
  };

  // -------- Submit --------
  const handleSubmit = async (values) => {
    if (!firm?.user_id) {
      toastError("Invalid firm record.");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();
      const append = (k, v) => {
        if (v !== undefined && v !== null && v !== "") formData.append(k, v);
      };

      append("name", values.name);
      append("email", values.email);
      append("contact_number", values.contact_number);
      append("address", values.address);
      append("status", values.status);

      // IDs are plain values (no labelInValue)
      append("country_id", values.country_id);
      append("state_id", values.state_id);
      append("city_id", values.city_id);

      // Optional password update:
      // - send only if provided (non-empty)
      // - NEVER send confirm_password
      if (values.password) {
        append("password", values.password);
      }

      if (imageFile) formData.append("image", imageFile);

      await updateFirm(firm.user_id, formData);

      toastSuccess("Firm updated successfully");
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error("Update error:", error?.response?.data || error?.message);
      toastError(error?.response?.data?.message || "Failed to update firm");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={<span style={{ fontSize: "1.25rem", fontWeight: 600 }}>Edit Firm</span>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      destroyOnClose
      maskClosable={false}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Card title="Basic Information" bordered={false}>
          <Row gutter={24}>
            <Col span={8}>
              <Upload.Dragger
                name="image"
                beforeUpload={handleImageBeforeUpload}
                accept="image/*"
                maxCount={1}
                showUploadList={false}
                style={{ width: "100%", marginBottom: 16 }}
              >
                {imagePreview ? (
                  <Avatar src={imagePreview} size={120} style={{ margin: "0 auto" }} />
                ) : (
                  <div style={{ padding: 16 }}>
                    <p className="ant-upload-drag-icon">
                      <UserOutlined style={{ fontSize: 32 }} />
                    </p>
                    <p className="ant-upload-text">Upload Firm Logo</p>
                  </div>
                )}
              </Upload.Dragger>
            </Col>

            <Col span={16}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Firm Name"
                    name="name"
                    rules={[{ required: true, message: "Please enter firm name" }]}
                  >
                    <Input placeholder="Firm Name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[{ required: true }, { type: "email", message: "Enter a valid email" }]}
                  >
                    <Input placeholder="Email address" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Contact Number" name="contact_number">
                    <Input placeholder="Contact number" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Status" name="status" rules={[{ required: true }]}>
                    <Select
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                      ]}
                      placeholder="Select status"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        <Card title="Location Information" bordered={false}>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Country" name="country_id">
                <Select
                  showSearch
                  onChange={handleCountryChange}
                  options={countries.map((c) => ({ value: c.id, label: c.name }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Select Country"
                  loading={loadingCountries}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="State" name="state_id">
                <Select
                  showSearch
                  onChange={handleStateChange}
                  options={states.map((s) => ({ value: s.id, label: s.name }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Select State"
                  loading={loadingStates}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="City" name="city_id">
                <Select
                  showSearch
                  options={cities.map((c) => ({ value: c.id, label: c.name }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Select City"
                  loading={loadingCities}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label="Address" name="address">
                <Input.TextArea rows={2} placeholder="Full address" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Optional Password Update */}
        <Card
          title="Security (Optional)"
          bordered={false}
          headStyle={{ border: "none", padding: "16px 0" }}
          bodyStyle={{ padding: "16px 0" }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="New Password"
                name="password"
                hasFeedback
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      // Optional: allow blank. If provided, enforce min length.
                      if (!value || value.length >= 6) return Promise.resolve();
                      return Promise.reject(new Error("Password must be at least 6 characters"));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Leave blank to keep current password" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Confirm New Password"
                name="confirm_password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const pwd = getFieldValue("password");
                      // If no new password was provided, confirmation is optional too.
                      if (!pwd && !value) return Promise.resolve();
                      if (pwd && value === pwd) return Promise.resolve();
                      return Promise.reject(new Error("Passwords do not match"));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Re-enter new password" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
          <Button style={{ marginRight: 16 }} onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={saving}>
            Update
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
